// src/lib/usePushNotifications.js
// Gestionează push notifications native (Capacitor + FCM)
import { useEffect } from 'react';
import api from './api';

const isNative = () =>
  typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();

/**
 * Hook ce trebuie apelat după autentificare (are nevoie de user logat).
 * Cere permisiunea, obține token-ul FCM și îl înregistrează la backend.
 */
export function usePushNotifications() {
  useEffect(() => {
    if (!isNative()) return;

    let listenerForeground = null;
    let listenerAction = null;

    import('@capacitor/push-notifications').then(({ PushNotifications }) => {
      // 1. Cere permisiunea
      PushNotifications.requestPermissions().then(({ receive }) => {
        if (receive === 'granted') {
          PushNotifications.register();
        }
      });

      // 2. Token FCM primit → trimitem la backend
      PushNotifications.addListener('registration', ({ value: token }) => {
        const platform = window.Capacitor?.getPlatform?.() === 'ios' ? 'ios' : 'android';
        api.post('/b/push-tokens/register', { token, platform }).catch(() => {});
      });

      // 3. Notificare primită în foreground → afișăm alert simplu (poți înlocui cu toast)
      listenerForeground = PushNotifications.addListener(
        'pushNotificationReceived',
        (notification) => {
          // App-ul e deschis: notificarea nu apare automat în sistem.
          // Poți declanșa un toast/banner custom din app.
          console.log('[Push] Foreground notification:', notification.title);
        }
      );

      // 4. User a apăsat pe notificare (app era în background/închis)
      listenerAction = PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action) => {
          const data = action.notification?.data || {};
          // Navighează la programarea relevantă
          if (data.appointment_id) {
            window.location.hash = '';
            // Folosim window.location pentru a forța reload în context nativ
            const current = window.location.pathname;
            if (!current.includes('appointment-calendar')) {
              window.location.replace('/app/appointment-calendar');
            }
          }
        }
      );
    }).catch(() => {});

    return () => {
      listenerForeground?.then?.(l => l?.remove?.());
      listenerAction?.then?.(l => l?.remove?.());
    };
  }, []);
}

/**
 * Dezînregistrează token-ul FCM al device-ului curent (apelat la logout).
 */
export async function unregisterPushToken() {
  if (!isNative()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { value: token } = await PushNotifications.getDeliveredNotifications()
      .then(() => PushNotifications.removeAllDeliveredNotifications())
      .then(() => ({ value: null }))
      .catch(() => ({ value: null }));

    // Cel mai simplu: obținem token-ul din re-register
    await new Promise((resolve) => {
      PushNotifications.addListener('registration', async ({ value }) => {
        await api.delete('/b/push-tokens/unregister', { data: { token: value } }).catch(() => {});
        resolve();
      });
      PushNotifications.register().catch(resolve);
    });
  } catch {}
}
